import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { COLLS } from '../../constants';

export default function PlaybooksPanel() {
  const { playbooks, go, setActivePbIdx, toast, currentUser } = useApp();
  const { isMobile } = useBreakpoint();
  const [view, setView] = useState<'created' | 'assigned'>('created');
  const [analyticsIdx, setAnalyticsIdx] = useState<number | null>(null);

  const createdPbs  = playbooks.filter(pb => pb.createdBy === currentUser?.name);
  const assignedPbs = playbooks.filter(pb => pb.assignedToMe);
  const displayed   = view === 'created' ? createdPbs : assignedPbs;

  return (
    <div style={{ paddingTop: '12px' }}>
      {/* View tabs */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '8px', flexWrap: 'wrap' }}>
        {(['assigned', 'created'] as const).map(v => (
          <button key={v} onClick={() => setView(v)} style={{ padding: '7px 12px', borderRadius: '8px', border: '1.5px solid var(--border2)', background: view === v ? 'var(--navy)' : 'white', color: view === v ? 'white' : 'var(--text2)', fontFamily: 'inherit', fontSize: '12px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <i className={`ti ${v === 'assigned' ? 'ti-user-check' : 'ti-pencil'}`} />
            {v === 'assigned' ? 'Assigned to me' : 'Created by me'}
            <span style={{ background: view === v ? 'rgba(255,255,255,.2)' : 'var(--tag-bg)', color: view === v ? 'white' : 'var(--text3)', fontSize: '10px', fontWeight: '700', borderRadius: '20px', padding: '1px 6px' }}>
              {v === 'assigned' ? assignedPbs.length : createdPbs.length}
            </span>
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {displayed.map((pb, idx) => {
          const globalIdx = playbooks.indexOf(pb);
          const colls = (pb.colls || []).map(i => COLLS[i]).filter(Boolean);
          return (
            <div key={idx} style={{ background: 'white', border: '1.5px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
              <div style={{ height: '4px', background: pb.dealRoom.color }} />
              <div style={{ padding: '12px 14px' }}>
                {/* Top row */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '4px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '14px', fontWeight: '700', color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: isMobile ? '180px' : undefined }}>{pb.name}</span>
                      <span style={{ fontSize: '9px', fontWeight: '700', padding: '2px 7px', borderRadius: '20px', background: pb.status === 'sent' ? '#F0FFF4' : 'var(--surface)', color: pb.status === 'sent' ? '#276749' : 'var(--text3)', border: `1px solid ${pb.status === 'sent' ? '#9AE6B4' : 'var(--border2)'}`, flexShrink: 0 }}>
                        {pb.status === 'sent' ? '✓ Sent' : '✎ Draft'}
                      </span>
                    </div>

                    {/* Deal room badge */}
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '20px', background: pb.dealRoom.color + '1A', fontSize: '10px', fontWeight: '600', color: pb.dealRoom.accent, marginBottom: '8px' }}>
                      <i className={`ti ${pb.dealRoom.stageIco}`} style={{ fontSize: '10px' }} />
                      {pb.dealRoom.stage} · {pb.dealRoom.label}
                    </div>

                    {/* Recipients */}
                    {pb.recipients.length > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', flexWrap: 'wrap' }}>
                        {pb.recipients.map((r, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: 'var(--text2)' }}>
                            <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: 'var(--navy)', color: 'var(--gold)', fontSize: '7px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{r.ini}</div>
                            {r.name}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Collaterals strip */}
                    {!isMobile && (
                      <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginBottom: '8px' }}>
                        {colls.map((c, i) => (
                          <div key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '2px 8px', borderRadius: '5px', background: 'var(--surface)', border: '1px solid var(--border)', fontSize: '10px', color: 'var(--text2)' }}>
                            <i className="ti ti-file" style={{ fontSize: '9px' }} /> {c.name.length > 20 ? c.name.substring(0, 20)+'…' : c.name}
                          </div>
                        ))}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: 'var(--text3)', flexWrap: 'wrap' }}>
                      <span><strong style={{ color: 'var(--navy)' }}>{pb.views}</strong> views</span>
                      <span><strong style={{ color: 'var(--navy)' }}>{pb.opens}</strong> opens</span>
                      {!isMobile && <span>By {pb.createdBy}</span>}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0, alignSelf: isMobile ? 'auto' : undefined, flexDirection: isMobile ? 'row' : 'column', width: isMobile ? '100%' : undefined, marginTop: isMobile ? '4px' : undefined }}>
                    <button onClick={() => { setActivePbIdx(globalIdx); go('client'); }} style={{ flex: isMobile ? 1 : undefined, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', padding: '7px 12px', borderRadius: '7px', background: 'var(--navy)', border: 'none', color: 'white', fontFamily: 'inherit', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>
                      <i className="ti ti-eye" /> Preview
                    </button>
                    <button onClick={() => setAnalyticsIdx(analyticsIdx === globalIdx ? null : globalIdx)} style={{ flex: isMobile ? 1 : undefined, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', padding: '7px 12px', borderRadius: '7px', border: '1.5px solid var(--border2)', background: 'white', fontFamily: 'inherit', fontSize: '11px', color: 'var(--text2)', cursor: 'pointer' }}>
                      <i className="ti ti-chart-bar" /> Analytics
                    </button>
                    {!isMobile && (
                      <button onClick={() => toast('Link copied!')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', padding: '7px 12px', borderRadius: '7px', border: '1.5px solid var(--border2)', background: 'white', fontFamily: 'inherit', fontSize: '11px', color: 'var(--text2)', cursor: 'pointer' }}>
                        <i className="ti ti-share" /> Share
                      </button>
                    )}
                  </div>
                </div>

                {/* Analytics drawer */}
                {analyticsIdx === globalIdx && (
                  <div style={{ marginTop: '12px', padding: '12px', background: 'var(--surface)', borderRadius: '10px', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--navy)', marginBottom: '10px' }}>Analytics</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '8px', marginBottom: '12px' }}>
                      {[{ label: 'Views', val: pb.views }, { label: 'Opens', val: pb.opens }, { label: 'Recipients', val: pb.recipients.length }, { label: 'Files', val: colls.length }].map((k, i) => (
                        <div key={i} style={{ background: 'white', borderRadius: '8px', padding: '8px', textAlign: 'center', border: '1px solid var(--border)' }}>
                          <div style={{ fontSize: '18px', fontWeight: '800', color: 'var(--navy)' }}>{k.val}</div>
                          <div style={{ fontSize: '9px', color: 'var(--text3)', fontWeight: '600', textTransform: 'uppercase' }}>{k.label}</div>
                        </div>
                      ))}
                    </div>
                    {pb.recipients.map((r, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', background: 'white', borderRadius: '8px', border: '1px solid var(--border)', marginBottom: '6px' }}>
                        <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: 'var(--navy)', color: 'var(--gold)', fontSize: '9px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{r.ini}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--navy)' }}>{r.name}</div>
                          <div style={{ fontSize: '10px', color: 'var(--text3)' }}>{r.org}</div>
                        </div>
                        <div style={{ fontSize: '10px', color: 'var(--text3)', textAlign: 'right' }}>
                          <div style={{ fontWeight: '600', color: 'var(--navy)' }}>{pb.views} views</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
